/* --------------------------------------------------------------------------
   Veckodagar och klockslag

   Ett urval av veckodagar och ett tidsfönster på dygnet, ovanpå den valda
   perioden: "alla fredagar 16–21", "måndag–onsdag", "fram till kl. 16". Båda
   hör till kvittot, precis som butik och kassa, och gäller därför också
   jämförelseperioden och utvecklingsvyn. Utvecklingen per vecka blir då en
   summa av de valda dagarna i varje vecka — tio helger ger tio staplar, inte
   trettio dagar.

   Veckodagarna numreras som weekdayOf(): måndag 0 … söndag 6.

   Klockslagen är svensk tid, samma som allt annat efter normalize().
   "Från" ingår och "till" ingår inte: 16:00–21:00 tar med 20:59:59 men inte
   21:00:00, så att två fönster som möts aldrig räknar samma kvitto två gånger.
   Är "från" senare än "till" går fönstret över midnatt. Kvittot hör ändå till
   den kalenderdag det slogs in. "Till 00:00" betyder midnatt i slutet av
   dygnet: 16:00–00:00 är kvällen, inte ett fönster över midnatt, och visas
   som "från kl. 16:00". "Från 00:00" är på samma sätt ingen nedre gräns.
   -------------------------------------------------------------------------- */

export const WEEKDAY_SHORT  = ['mån','tis','ons','tor','fre','lör','sön'];
/** Utan å, ä och ö — för adressen och filnamn. */
export const WEEKDAY_SLUG   = ['man','tis','ons','tor','fre','lor','son'];
const WEEKDAY_PLURAL = ['måndagar','tisdagar','onsdagar','torsdagar','fredagar','lördagar','söndagar'];

const CLOCK = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

/** "16:00" → sekunder efter midnatt, 57600. Ger null för tomt eller ogiltigt. */
export function parseClock(v){
  const m = typeof v === 'string' ? v.trim().match(CLOCK) : null;
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] || 0) : null;
}

/** 57600 → "16:00". */
export function fmtClock(sec){
  const h = Math.floor(sec / 3600), m = Math.floor(sec % 3600 / 60);
  return String(h).padStart(2,'0') + ':' + String(m).padStart(2,'0');
}

/**
 * Valda veckodagar som Set, eller null när alla dagar gäller. Inga valda och
 * alla sju valda betyder samma sak: inget urval.
 */
export function selectedDays(weekdays){
  const set = new Set((weekdays || []).map(Number).filter(d => Number.isInteger(d) && d >= 0 && d <= 6));
  return set.size === 0 || set.size === 7 ? null : set;
}

/**
 * Tidsfönstret som {from, to, wraps} i sekunder, där from eller to kan vara
 * null. Ger null när inget fönster gäller. Samma klockslag i båda fälten är
 * ett tomt fönster, inte ett helt dygn — det behandlas som inget urval, och
 * gränssnittet förklarar varför (se clockProblem).
 */
export function clockWindow(timeFrom, timeTo){
  let from = parseClock(timeFrom), to = parseClock(timeTo);
  if(from === 0) from = null;   // från 00:00: ingen nedre gräns
  if(to === 0)   to = null;     // till 00:00: dygnets slut, ingen övre gräns
  if(from === null && to === null) return null;
  if(from === to) return null;
  return {from, to, wraps: from !== null && to !== null && from > to};
}

/** Steget mellan klockslagen i tidsväljaren: halvtimmar. */
export const CLOCK_STEP = 1800;

/**
 * Klockslagen i tidsväljaren, 24-timmarsformat: "00:30", "01:00" … "23:30".
 * 00:00 saknas med avsikt — det är fältets tomma värde, ingen gräns.
 */
export function clockOptions(step = CLOCK_STEP){
  const out = [];
  for(let s = step; s < 86400; s += step) out.push(fmtClock(s));
  return out;
}

/**
 * Ett klockslag som tidsväljarens värde: "16:00", eller '' för 00:00 och
 * för det som inte går att tolka. Sekunder tas bort, "9:05" blir "09:05".
 */
export function clockValue(v){
  const s = parseClock(v);
  return s ? fmtClock(s) : '';
}

/** Förklaring när fälten inte går att använda, annars ''. */
export function clockProblem(timeFrom, timeTo){
  const from = parseClock(timeFrom), to = parseClock(timeTo);
  if(from !== null && from === to) return 'Från och till är samma klockslag, så tiden begränsar inte urvalet.';
  if(from === null && to === 0) return 'Till kl. 00:00 är midnatt i slutet av dygnet, så tiden begränsar inte urvalet.';
  return '';
}

/** Sekunder efter midnatt ur "ÅÅÅÅ-MM-DDTtt:mm:ss". */
function secondsOf(ts){
  return Number(ts.slice(11,13)) * 3600 + Number(ts.slice(14,16)) * 60 + (Number(ts.slice(17,19)) || 0);
}

/**
 * Funktion som avgör om ett kvitto ligger inom veckodagarna och klockslagen,
 * eller null när inget av dem är valt. f är filtret: {weekdays, timeFrom, timeTo}.
 */
export function receiptPredicate(f){
  const days = selectedDays(f.weekdays);
  const win  = clockWindow(f.timeFrom, f.timeTo);
  if(!days && !win) return null;

  let inWindow = null;
  if(win){
    const {from, to, wraps} = win;
    inWindow = from === null ? s => s < to
             : to   === null ? s => s >= from
             : wraps         ? s => s >= from || s < to
             :                 s => s >= from && s < to;
  }
  return r => (!days || days.has(r.weekday)) && (!inWindow || inWindow(secondsOf(r.ts)));
}

/** Veckodag för "ÅÅÅÅ-MM-DD", måndag 0. */
function weekdayOfDay(iso){
  const [y,m,d] = iso.split('-').map(Number);
  return (new Date(Date.UTC(y, m-1, d)).getUTCDay() + 6) % 7;
}

/**
 * Antal kalenderdagar från och med a till och med b som är någon av de valda
 * veckodagarna. days null räknar alla dagar. Används för att avgöra om en
 * vecka eller månad är komplett: med bara fredagar valda är en vecka hel när
 * dess fredag finns i filen, inte när alla sju dagar gör det.
 */
export function countDays(a, b, days){
  if(!a || !b || a > b) return 0;
  const [y1,m1,d1] = a.split('-').map(Number);
  const [y2,m2,d2] = b.split('-').map(Number);
  const total = Math.round((Date.UTC(y2, m2-1, d2) - Date.UTC(y1, m1-1, d1)) / 86400000) + 1;
  if(!days) return total;
  const weeks = Math.floor(total / 7);
  let n = weeks * days.size;
  const start = weekdayOfDay(a);
  for(let i = 0; i < total % 7; i++) if(days.has((start + i) % 7)) n++;
  return n;
}

/**
 * Valda veckodagar i ord: "fredagar", "mån–ons", "fre–sön", "mån, ons och fre".
 * Tre eller fler dagar i följd skrivs som ett spann. Ger '' när alla dagar gäller.
 */
export function describeDays(weekdays){
  const days = selectedDays(weekdays);
  if(!days) return '';
  const list = [...days].sort((a,b) => a-b);
  if(list.length === 1) return WEEKDAY_PLURAL[list[0]];

  const parts = [];
  for(let i = 0; i < list.length; ){
    let j = i;
    while(j + 1 < list.length && list[j + 1] === list[j] + 1) j++;
    if(j - i >= 2) parts.push(WEEKDAY_SHORT[list[i]] + '–' + WEEKDAY_SHORT[list[j]]);
    else for(let k = i; k <= j; k++) parts.push(WEEKDAY_SHORT[list[k]]);
    i = j + 1;
  }
  return parts.length === 1 ? parts[0]
    : parts.slice(0, -1).join(', ') + ' och ' + parts[parts.length - 1];
}

/** Tidsfönstret i ord: "kl. 16:00–21:00", "före kl. 16:00", "från kl. 16:00". */
export function describeClock(timeFrom, timeTo){
  const w = clockWindow(timeFrom, timeTo);
  if(!w) return '';
  if(w.from === null) return 'före kl. ' + fmtClock(w.to);
  if(w.to === null)   return 'från kl. ' + fmtClock(w.from);
  return `kl. ${fmtClock(w.from)}–${fmtClock(w.to)}` + (w.wraps ? ' (över midnatt)' : '');
}

/** Veckodagar och klockslag i en rad, med stor bokstav. '' utan urval. */
export function describeDayTime(f){
  const t = [describeDays(f.weekdays), describeClock(f.timeFrom, f.timeTo)].filter(Boolean).join(', ');
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
}
